import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Users, FileText, Building, ChevronRight } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function ProblemSection() {
  const goToCheckout = () => {
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Guía Maximiza tu Seguro Social',
        content_type: 'product',
        value: 27.0,
        currency: 'USD'
      });
    }
    window.open('https://tiendadelibrosdigital.com/cart/42545816797255:1', '_blank');
  };
  return <section className="py-20 bg-[#FDF8F0]" data-id="element-129">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-130">
        {/* Hook Statistic */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="bg-white p-8 md:p-12 rounded-2xl shadow-lg border-t-4 border-[#C5A55A] mb-16 relative overflow-hidden" data-id="element-131">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#C5A55A]/5 rounded-bl-full -z-0" data-id="element-132" />
          <p className="text-xl md:text-2xl text-[#2D2D2D] font-medium leading-relaxed relative z-10" data-id="element-133">
            ¿Sabía usted que el 80% de las mujeres latinas en los Estados Unidos
            pierden hasta{' '}
            <strong className="text-[#C5A55A] text-3xl md:text-4xl block my-2 font-serif" data-id="element-134">
              $100,000 dólares
            </strong>{' '}
            a lo largo de su jubilación simplemente por tomar una decisión
            apresurada a los 62 años?
          </p>
          <p className="mt-6 text-lg text-gray-600" data-id="element-135">
            No es su culpa. El sistema está diseñado para ser confuso, pero en
            esta "Nueva Era" de 2026, las reglas han cambiado. Dejar su
            seguridad financiera en manos de la suerte o de un funcionario con
            prisa ya no es una opción.
          </p>
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={goToCheckout} className="mt-8 w-full sm:w-auto flex items-center justify-center gap-2 bg-[#C5A55A] hover:bg-[#b39550] text-[#0F1D33] text-lg font-bold py-4 px-8 rounded-full shadow-[0_0_20px_rgba(197,165,90,0.3)] transition-all duration-300 relative z-10" aria-label="Ver precio y descargar la guía" data-id="element-136">
            QUIERO PROTEGER MI JUBILACIÓN
            <ChevronRight className="w-5 h-5" data-id="element-137" />
          </motion.button>
        </motion.div>

        {/* The Problem Description */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="space-y-6 text-lg text-gray-700 mb-20" data-id="element-138">
          <p data-id="element-139">
            Usted ha trabajado duro durante décadas. Ha cuidado de su familia,
            ha pagado sus impuestos y ha contribuido al sistema con la esperanza
            de una vejez tranquila.
          </p>
          <p data-id="element-140">Sin embargo, al acercarse la fecha, surge el miedo:</p>
          <blockquote className="border-l-4 border-[#C5A55A] pl-6 py-2 my-8 bg-white/50 italic text-xl text-[#1B2A4A] font-serif" data-id="element-141">
            "¿Estaré tomando la decisión correcta? ¿Qué pasa si me jubilo ahora
            y luego me falta el dinero? ¿Por qué nadie me explica mis derechos
            en español de forma clara?"
          </blockquote>
          <p data-id="element-142">
            Tomar su jubilación antes de tiempo por impulso, o no reclamar los
            beneficios de un ex-esposo por no saber que tiene el derecho, son
            errores que le costarán miles de dólares cada año, de forma
            permanente.
          </p>
        </motion.div>

        {/* Why other approaches failed */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={stagger} data-id="element-143">
          <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl font-bold text-[#1B2A4A] mb-10 text-center" data-id="element-144">
            Por qué lo que intentó no funcionó
          </motion.h2>

          <div className="space-y-6" data-id="element-145">
            {/* Item 1 */}
            <motion.div variants={fadeIn} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 flex gap-6 items-start relative overflow-hidden group hover:shadow-md transition-shadow" data-id="element-146">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#C5A55A]" data-id="element-147" />
              <div className="bg-[#FDF8F0] p-4 rounded-full text-[#C5A55A] shrink-0" data-id="element-148">
                <Users className="w-8 h-8" data-id="element-149" />
              </div>
              <div data-id="element-150">
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-2" data-id="element-151">
                  Consejos de amigas
                </h3>
                <p className="text-gray-600" data-id="element-152">
                  Sus vecinas tienen buena intención, pero el historial laboral
                  de cada persona es un mundo distinto; lo que le funcionó a
                  ella podría arruinarla a usted.
                </p>
              </div>
            </motion.div>

            {/* Item 2 */}
            <motion.div variants={fadeIn} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 flex gap-6 items-start relative overflow-hidden group hover:shadow-md transition-shadow" data-id="element-153">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#C5A55A]" data-id="element-154" />
              <div className="bg-[#FDF8F0] p-4 rounded-full text-[#C5A55A] shrink-0" data-id="element-155">
                <FileText className="w-8 h-8" data-id="element-156" />
              </div>
              <div data-id="element-157">
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-2" data-id="element-158">
                  Manuales oficiales
                </h3>
                <p className="text-gray-600" data-id="element-159">
                  Leer los folletos de la SSA es como tratar de leer un contrato
                  legal en otro idioma. Es agotador, lleno de jerga técnica y
                  sumamente confuso.
                </p>
              </div>
            </motion.div>

            {/* Item 3 */}
            <motion.div variants={fadeIn} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 flex gap-6 items-start relative overflow-hidden group hover:shadow-md transition-shadow" data-id="element-160">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#C5A55A]" data-id="element-161" />
              <div className="bg-[#FDF8F0] p-4 rounded-full text-[#C5A55A] shrink-0" data-id="element-162">
                <Building className="w-8 h-8" data-id="element-163" />
              </div>
              <div data-id="element-164">
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-2" data-id="element-165">
                  Confiar ciegamente en el agente
                </h3>
                <p className="text-gray-600" data-id="element-166">
                  Los agentes de la oficina procesan solicitudes, no son sus
                  asesores financieros. Ellos no le dirán cómo maximizar su
                  cheque, solo cómo tramitarlo.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}